import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Checkbox,
    MenuItem,
    Select
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import { Transition } from 'Shared/Transition';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import http from 'Utils/http';
import config from '../../../../config';
import { Controller, useForm } from 'react-hook-form';
import Editor from 'react-simple-code-editor';
import JSZip from 'jszip';
import { highlight, languages } from 'prismjs/components/prism-core';
import { copyText } from 'Utils/clipboard';
import { useNavigate } from 'react-router-dom';

const normalizeIssues = (issues) => {
    if (!issues) {
        return [];
    }

    if (Array.isArray(issues)) {
        return issues;
    }

    if (typeof issues === 'object') {
        return Object.values(issues);
    }

    return [];
};

const DEFAULT_ISSUE_MESSAGE = 'Need to fix errors in JSON';

const buildIssueMessage = (issues = []) =>
    issues
        .map(({ message, pointer }) => (pointer ? `${message} (${pointer})` : message))
        .filter(Boolean)
        .join('; ');

const formatImportError = (payload, fallback = 'Unknown error') => {
    if (!payload) {
        return fallback;
    }

    const normalizedIssues = normalizeIssues(payload.issues);
    if (normalizedIssues.length > 0) {
        return payload.message || DEFAULT_ISSUE_MESSAGE;
    }

    const issueMessage = buildIssueMessage(normalizedIssues);
    return issueMessage || payload.error || payload.message || fallback;
};

const FlowExportFlow = ({ visible, setVisible, update, folderId, onFoldersChanged }) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [code, setCode] = useState('');
    const [importIssues, setImportIssues] = useState([]);
    const [dryRun, setDryRun] = useState(false);
    const [recreateFolders, setRecreateFolders] = useState(true);
    const [aiPrompt, setAiPrompt] = useState('');
    const [editorFocused, setEditorFocused] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [files, setFiles] = useState([]);
    const [conflicts, setConflicts] = useState([]);
    const hiddenTextareaRef = useRef(null);

    const {
        control,
        setValue,
        getValues,
        formState: { errors }
    } = useForm({
        defaultValues: {
            code: ''
        }
    });

    const onClose = () => {
        setCode('');
        setImportIssues([]);
        setDryRun(false);
        setRecreateFolders(true);
        setAiPrompt('');
        setFiles([]);
        setConflicts([]);
        setVisible(false);
    };

    const writePromptToHiddenTextarea = (prompt) => {
        const textArea = hiddenTextareaRef.current;
        if (textArea && prompt) {
            textArea.value = prompt;
        }
    };

    const copyToClipboard = () => {
        const textArea = hiddenTextareaRef.current;
        if (!textArea || !textArea.value) {
            enqueueSnackbar('Nothing to copy', { action: CustomSnackbar, variant: 'error' });
            return;
        }

        copyText(textArea.value)
            .then(() => {
                enqueueSnackbar('Prompt copied! Paste it into ChatGPT or Claude.', {
                    action: CustomSnackbar,
                    variant: 'success'
                });
            })
            .catch(() => {
                enqueueSnackbar('Failed to copy', { action: CustomSnackbar, variant: 'error' });
            });
    };

    const readAsText = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
    });

    // Files of an archive are joined as raw text on purpose: whatever is wrong
    // inside them is reported by the import itself, with a place in the JSON.
    // A picked archive turns into the json files it holds; everything the user
    // adds keeps its name, so an error can point at the file it came from.
    const expandFile = async (file) => {
        if (! /\.zip$/i.test(file.name)) {
            return [{ name: file.name, text: await readAsText(file) }];
        }

        const zip = await JSZip.loadAsync(file);
        const entries = Object.values(zip.files).filter((entry) => !entry.dir && /\.json$/i.test(entry.name));

        return Promise.all(entries.map(async (entry) => ({
            name: `${file.name}/${entry.name}`,
            text: await entry.async('string'),
        })));
    };

    // Everything collected so far becomes one flow package in the editor. The
    // first file that is not JSON stops it and is reported with its own text.
    const buildPackage = async (packageFiles) => {
        const flows = [];

        for (const file of packageFiles) {
            let parsed;
            try {
                parsed = JSON.parse(file.text);
            } catch {
                await reportBrokenFile(file);
                return;
            }

            flows.push(...(Array.isArray(parsed) ? parsed : [parsed]));
        }

        setImportIssues([]);
        setAiPrompt('');

        const text = flows.length === 1 ? JSON.stringify(flows[0], null, 4) : JSON.stringify(flows, null, 4);
        setCode(flows.length ? text : '');
        setValue('code', flows.length ? text : '');
    };

    const handleFileChange = async (event) => {
        const picked = Array.from(event.target.files);
        event.target.value = '';

        if (!picked.length) {
            return;
        }

        try {
            const added = (await Promise.all(picked.map(expandFile))).flat();
            const nextFiles = [...files, ...added];
            setFiles(nextFiles);
            await buildPackage(nextFiles);
        } catch {
            enqueueSnackbar('Unable to read the files.', {
                action: CustomSnackbar,
                variant: 'error',
            });
        }
    };

    const removeFile = async (index) => {
        const nextFiles = files.filter((file, position) => position !== index);
        setFiles(nextFiles);
        await buildPackage(nextFiles);
    };

    const showFailure = (payload, source = null) => {
        const issues = normalizeIssues(payload?.issues);
        if (issues.length === 0 && (payload?.error || payload?.message)) {
            issues.push({
                message: payload.error || payload.message,
                pointer: payload.trace?.[0]?.file ? `${payload.trace[0].file}:${payload.trace[0].line}` : null
            });
        }
        setImportIssues(source ? issues.map((issue) => ({ ...issue, message: `${source}: ${issue.message}` })) : issues);
        setAiPrompt(payload?.ai_prompt || '');
        writePromptToHiddenTextarea(payload?.ai_prompt || '');
        enqueueSnackbar(`Error sending data: ${formatImportError(payload)}.`, {
            action: CustomSnackbar,
            variant: 'error',
        });
    };

    // The file is sent as it is, so the error and the prompt for the AI are
    // built from its own text by the same place that reports every other one.
    const reportBrokenFile = async (file) => {
        try {
            const { data } = await http.post('/flow/import', file.text, { params: { dry_run: true } });
            showFailure(data, file.name);
        } catch (err) {
            showFailure(err.response?.data ?? { message: err.message }, file.name);
        }
    };

    const parsePackage = (text) => {
        try {
            const parsed = JSON.parse(text);

            return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            return null;
        }
    };

    // What the import would replace is known before it runs, so the choice is
    // made here and the Import button is the last thing the user does.
    useEffect(() => {
        const flows = parsePackage(code);
        const externalIds = (flows ?? []).map((flow) => flow?.externalID).filter(Boolean);

        if (!externalIds.length) {
            setConflicts([]);
            return undefined;
        }

        const timer = setTimeout(() => {
            http.post('/flow/external-ids', { external_ids: externalIds })
                .then(({ data }) => {
                    setConflicts(Object.entries(data.existing || {}).map(([externalId, existing]) => ({
                        externalId,
                        name: flows.find((flow) => flow?.externalID === externalId)?.name,
                        existing,
                        action: 'replace',
                    })));
                })
                .catch(() => setConflicts([]));
        }, 400);

        return () => clearTimeout(timer);
    }, [code]);

    const hasFolderPaths = useMemo(
        () => (parsePackage(code) ?? []).some((flow) => Array.isArray(flow?.folderPath) && flow.folderPath.length > 0),
        [code]
    );

    const setConflictAction = (externalId, action) => {
        setConflicts((prev) => prev.map((row) => (row.externalId === externalId ? { ...row, action } : row)));
    };

    const targetOf = (flow) => {
        const chosen = conflicts.find((row) => row.externalId === flow?.externalID);

        return chosen && chosen.action === 'replace' ? `/flow/${chosen.existing.id}/import` : '/flow/import';
    };

    const duplicatedExternalIds = (flows) => {
        const seen = new Set();
        const duplicated = new Set();
        flows.forEach((flow) => {
            const externalId = flow?.externalID;
            if (!externalId) {
                return;
            }
            if (seen.has(externalId)) {
                duplicated.add(externalId);
            }
            seen.add(externalId);
        });

        return [...duplicated];
    };

    const handleUploadSubmit = async () => {
        const codeValue = getValues('code');
        if (!codeValue.trim()) {
            enqueueSnackbar('Please enter JSON data', {
                action: CustomSnackbar,
                variant: 'error',
            });
            return;
        }

        const flows = parsePackage(codeValue);
        setIsImporting(true);

        try {
            // Broken JSON is reported by the import itself, with its own prompt.
            if (!flows) {
                const { data } = await http.post('/flow/import', codeValue, { params: { dry_run: true } });
                showFailure(data);
                return;
            }

            const duplicated = duplicatedExternalIds(flows);
            if (duplicated.length) {
                setImportIssues([{
                    message: `The same External ID is used by more than one flow of this import: ${duplicated.join(', ')}`,
                }]);
                enqueueSnackbar('Duplicate External ID in the import', {
                    action: CustomSnackbar,
                    variant: 'error',
                });
                return;
            }

            const { data: { flows: prepared } } = await http.post('/flows/import/prepare', {
                flows,
                replace_external_ids: conflicts.filter((row) => row.action === 'replace').map((row) => row.externalId),
            });

            // Nothing is written until every flow of the package passes.
            for (const flow of prepared) {
                try {
                    const { data } = await http.post(targetOf(flow), JSON.stringify(flow), { params: { dry_run: true } });
                    if (data.status !== 'success') {
                        showFailure(data, flow?.name);
                        return;
                    }
                } catch (err) {
                    showFailure(err.response?.data ?? { message: err.message }, flow?.name);
                    return;
                }
            }

            setImportIssues([]);
            setAiPrompt('');

            if (dryRun) {
                enqueueSnackbar(`Validation successful${flows.length > 1 ? ` for ${flows.length} flows` : ''}! No data was saved (dry run mode).`, {
                    action: CustomSnackbar,
                    variant: 'success',
                });
                return;
            }

            const importedIds = [];
            const created = [];
            for (const flow of prepared) {
                try {
                    const target = targetOf(flow);
                    const { data } = await http.post(target, JSON.stringify(flow));
                    if (data.status !== 'success') {
                        showFailure(data, flow?.name);
                        return;
                    }
                    const importedId = target === '/flow/import' ? data.data?.id : conflicts.find((row) => row.externalId === flow?.externalID)?.existing.id;
                    importedIds.push(importedId);
                    if (target === '/flow/import' && importedId) {
                        created.push({ id: importedId, folderPath: flow?.folderPath });
                    }
                } catch (err) {
                    showFailure(err.response?.data ?? { message: err.message }, flow?.name);
                    return;
                }
            }

            const openFolderId = Number.isInteger(folderId) ? folderId : null;
            let foldersChanged = false;
            for (const { id, folderPath } of created) {
                const recreate = recreateFolders && Array.isArray(folderPath) && folderPath.length > 0;
                const targetFolderId = recreate
                    ? (await http.post('/folders/path', { path: folderPath, parent_folder_id: openFolderId })).data.folder_id
                    : openFolderId;
                foldersChanged = foldersChanged || recreate;
                if (targetFolderId !== null) {
                    await http.post(`/flow/${id}/folder`, { folder_id: targetFolderId });
                }
            }

            enqueueSnackbar(
                flows.length > 1
                    ? `${flows.length} flows have been imported.`
                    : `Flow - ${flows[0]?.name} has been imported.`,
                { action: CustomSnackbar, variant: 'success' }
            );
            onClose();
            update(true);
            if (foldersChanged) {
                onFoldersChanged?.();
            }
            if (prepared.length === 1 && importedIds[0]) {
                navigate(`/flow/${importedIds[0]}`);
            }
        } catch (err) {
            showFailure(err.response?.data ?? { message: err.message });
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <>
        <Dialog
            open={visible}
            onClose={onClose}
            TransitionComponent={Transition}
            sx={{
                '& .MuiDialog-paper': {
                    width: '960px',
                    maxWidth: '100%',
                    maxHeight: '90vh',
                    margin: '16px',
                },
            }}
        >
            <Box component="form">
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '8px',
                    }}
                >
                    <Box component="span" sx={{ color: '#3B465C', fontWeight: 600 }}>
                        Import Flow
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={onClose} variant="outlined" size="small" disabled={isImporting}>
                            Close
                        </Button>
                        <Button
                            onClick={handleUploadSubmit}
                            variant="contained"
                            size="small"
                            disabled={isImporting}
                            startIcon={isImporting ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {isImporting ? 'Importing...' : 'Import'}
                        </Button>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {/* File Upload & Validate Only */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: 2 }}>
                            <input
                                type="file"
                                multiple
                                accept=".json,application/json,.zip,application/zip"
                                onChange={handleFileChange}
                                disabled={isImporting}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={dryRun}
                                        onChange={(e) => setDryRun(e.target.checked)}
                                        disabled={isImporting}
                                        sx={{
                                            color: '#FF9800',
                                            '&.Mui-checked': {
                                                color: '#FF9800',
                                            },
                                        }}
                                    />
                                }
                                label="Validate only (without saving)"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        color: '#3B465C',
                                        fontWeight: dryRun ? 600 : 400,
                                    },
                                }}
                            />
                            {hasFolderPaths && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={recreateFolders}
                                            onChange={(e) => setRecreateFolders(e.target.checked)}
                                            disabled={isImporting}
                                        />
                                    }
                                    label="Recreate folder structure"
                                    sx={{ '& .MuiFormControlLabel-label': { color: '#3B465C' } }}
                                />
                            )}
                        </Box>

                        {files.length > 0 && (
                            <Box sx={{ marginBottom: '16px' }}>
                                {files.map((file, index) => (
                                    <Box
                                        key={`${file.name}-${index}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '4px 12px',
                                            marginBottom: '4px',
                                            backgroundColor: '#F2F5F8',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0, color: '#3B465C', overflowWrap: 'anywhere' }}>
                                            {file.name}
                                        </Box>
                                        <Button
                                            onClick={() => removeFile(index)}
                                            size="small"
                                            variant="outlined"
                                            disabled={isImporting}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {conflicts.length > 0 && (
                            <Box
                                sx={{
                                    marginBottom: '16px',
                                    padding: '12px',
                                    border: '1px solid #ffe082',
                                    borderRadius: '4px',
                                    backgroundColor: '#fff8e1',
                                }}
                            >
                                <Box sx={{ color: '#e65100', fontWeight: 600, marginBottom: '8px' }}>
                                    These flows are already here. Choose what the import does with them:
                                </Box>
                                {conflicts.map((row) => (
                                    <Box
                                        key={row.externalId}
                                        sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0, color: '#3B465C', overflowWrap: 'anywhere' }}>
                                            <div>{row.name}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#888' }}>{row.externalId}</div>
                                        </Box>
                                        <Select
                                            value={row.action}
                                            onChange={(e) => setConflictAction(row.externalId, e.target.value)}
                                            size="small"
                                            disabled={isImporting}
                                            sx={{ minWidth: '260px', backgroundColor: '#fff' }}
                                        >
                                            <MenuItem value="replace">Replace &quot;{row.existing.name}&quot;</MenuItem>
                                            <MenuItem value="create">Create new flow</MenuItem>
                                        </Select>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* JSON Editor */}
                        <FormControl sx={{ width: '100%', marginBottom: '12px' }}>
                            <Controller
                                name="code"
                                control={control}
                                rules={{
                                    required: 'JSON data is required',
                                }}
                                render={({ field }) => (
                                    <>
                                        <div style={{
                                            maxHeight: '50vh',
                                            overflow: 'auto',
                                            borderRadius: '4px',
                                            borderColor: errors.code ? '#f44336': 'rgba(0, 0, 0, 0.23)',
                                            borderWidth: '1px',
                                            borderStyle: 'solid',
                                        }}>
                                            <Editor
                                                {...field}
                                                value={code}
                                                disabled={isImporting}
                                                onValueChange={(newCode) => {
                                                    setCode(newCode);
                                                    setValue('code', newCode);
                                                }}
                                                onFocus={() => setEditorFocused(true)}
                                                onBlur={() => setEditorFocused(false)}
                                                highlight={(newCode) => highlight(newCode, languages.js)}
                                                padding={10}
                                                tabSize={4}
                                                style={{
                                                    backgroundColor: editorFocused ? '#E8F0FE' : '#F2F5F8',
                                                    minHeight: '200px',
                                                }}
                                                textareaClassName="editor-textarea-no-outline"
                                            />
                                        </div>
                                        {errors.code && (
                                            <FormHelperText error>
                                                {errors.code.message}
                                            </FormHelperText>
                                        )}
                                    </>
                                )}
                            />
                        </FormControl>
                        {importIssues.length > 0 && (
                            <Box
                                sx={{
                                    backgroundColor: '#fdecea',
                                    border: '1px solid #f5c2c0',
                                    borderRadius: '4px',
                                    padding: '12px',
                                    color: '#b3261e',
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <strong>Import issues:</strong>
                                    {aiPrompt && config.AI_ENABLED && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="secondary"
                                            startIcon={<ContentCopyIcon />}
                                            onClick={copyToClipboard}
                                        >
                                            Copy for AI
                                        </Button>
                                    )}
                                </Box>
                                <ul style={{ margin: '8px 0 0 16px' }}>
                                    {importIssues.map((issue, index) => (
                                        <li key={`${issue.pointer}-${index}`} style={{ marginBottom: '8px' }}>
                                            <div>
                                                {issue.message}
                                                {issue.pointer && (
                                                    <span style={{ color: '#7b1f19' }}> ({issue.pointer})</span>
                                                )}
                                                {(issue.line || issue.column) && (
                                                    <span style={{ color: '#7b1f19' }}>
                                                        {' '}
                                                        [line {issue.line ?? '?'}{issue.column ? `, col ${issue.column}` : ''}]
                                                    </span>
                                                )}
                                            </div>
                                            {issue.valueFragment && (
                                                <pre
                                                    style={{
                                                        margin: '4px 0 0',
                                                        background: '#fff',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #f5c2c0',
                                                        whiteSpace: 'pre-wrap',
                                                    }}
                                                >
{`${issue.valueFragment}  <-- ${issue.message}`}
                                                </pre>
                                            )}
                                            {issue.docLink && (
                                                <div style={{ marginTop: '8px' }}>
                                                    <a
                                                        href={issue.docLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: '#3498db',
                                                            fontWeight: 600,
                                                            textDecoration: 'none',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        📖 See documentation
                                                    </a>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
            </Box>
        </Dialog>
        <textarea
            ref={hiddenTextareaRef}
            style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
            tabIndex={-1}
            aria-hidden="true"
        />
        <style>{`
            .editor-textarea-no-outline:focus {
                outline: none !important;
            }
        `}</style>
        </>
    );
};

export default FlowExportFlow;
